import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { ResendConfirmationModal } from '../components/orders/ResendConfirmationModal';
import { mockOrders } from '../utils/mockData';
import { ArrowLeft, Mail, Printer, Package, Clock } from 'lucide-react';
import { useToast } from '../hooks/useToast';
interface ActivityLogEntry {
  id: string;
  timestamp: string;
  action: string;
}
export function OrderDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    showToast
  } = useToast();
  const order = mockOrders.find(o => o.id === id);
  const [isResendModalOpen, setIsResendModalOpen] = useState(false);
  const [activityLog, setActivityLog] = useState<ActivityLogEntry[]>([{
    id: 'log_1',
    timestamp: new Date(order?.createdAt || Date.now()).toISOString(),
    action: 'Order received'
  }]);
  if (!order) {
    return <div className="text-center py-12" data-id="element-739">
        <p className="text-gray-500" data-id="element-740">Order not found</p>
        <Link to="/orders" className="text-red-600 hover:text-red-700 mt-4 inline-block" data-id="element-741">
          Back to Orders
        </Link>
      </div>;
  }
  const handleResendConfirmation = (sendSms: boolean) => {
    const newEntry: ActivityLogEntry = {
      id: `log_${Date.now()}`,
      timestamp: new Date().toISOString(),
      action: sendSms ? `Confirmation resent to ${order.customerEmail} and SMS sent` : `Confirmation resent to ${order.customerEmail}`
    };
    setActivityLog(prev => [...prev, newEntry]);
    showToast(`Confirmation resent to ${order.customerEmail}`, 'success');
  };
  const handlePrintPackingSlip = () => {
    navigate(`/orders/${id}/packing-slip`);
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'received':
        return 'blue';
      case 'in-production':
        return 'yellow';
      case 'shipped':
        return 'green';
      case 'delivered':
        return 'green';
      default:
        return 'gray';
    }
  };
  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'green';
      case 'pending':
        return 'yellow';
      case 'failed':
        return 'red';
      default:
        return 'gray';
    }
  };
  return <div className="space-y-6" data-id="element-742">
      <div className="flex items-center justify-between" data-id="element-743">
        <div className="flex items-center space-x-4" data-id="element-744">
          <Link to="/orders" className="text-gray-500 hover:text-gray-700" data-id="element-745">
            <ArrowLeft className="h-5 w-5" data-id="element-746" />
          </Link>
          <div data-id="element-747">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-748">
              Order #{order.id}
            </h1>
            <p className="text-sm text-gray-500" data-id="element-749">
              Placed {new Date(order.createdAt).toLocaleString()}
            </p>
          </div>
        </div>

        <div className="flex space-x-3" data-id="element-750">
          <Button variant="outline" onClick={() => setIsResendModalOpen(true)} data-id="element-751">
            <Mail className="h-4 w-4 mr-2" data-id="element-752" />
            Resend Confirmation
          </Button>
          <Button variant="secondary" onClick={handlePrintPackingSlip} data-id="element-753">
            <Printer className="h-4 w-4 mr-2" data-id="element-754" />
            Print Packing Slip
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-755">
        <div className="lg:col-span-2 space-y-6" data-id="element-756">
          <Card title="Order Details" data-id="element-757">
            <div className="space-y-4" data-id="element-758">
              <div className="flex items-center justify-between" data-id="element-759">
                <span className="text-sm text-gray-600" data-id="element-760">Status</span>
                <Badge variant={getStatusColor(order.status)} data-id="element-761">
                  {order.status.toUpperCase()}
                </Badge>
              </div>
              <div className="flex items-center justify-between" data-id="element-762">
                <span className="text-sm text-gray-600" data-id="element-763">Payment Status</span>
                <Badge variant={getPaymentStatusColor(order.paymentStatus)} data-id="element-764">
                  {order.paymentStatus.toUpperCase()}
                </Badge>
              </div>
              <div className="flex items-center justify-between" data-id="element-765">
                <span className="text-sm text-gray-600" data-id="element-766">
                  Fulfillment Method
                </span>
                <span className="text-sm font-medium text-gray-900" data-id="element-767">
                  {order.fulfillmentMethod === 'ship-to-home' ? 'Ship to Home' : 'Bulk Ship'}
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-768">
                <span className="text-sm text-gray-600" data-id="element-769">Total</span>
                <span className="text-lg font-bold text-gray-900" data-id="element-770">
                  ${order.total.toFixed(2)}
                </span>
              </div>
            </div>
          </Card>

          <Card title="Items" data-id="element-771">
            <div className="space-y-4" data-id="element-772">
              <div className="flex items-start space-x-4 pb-4 border-b border-gray-200" data-id="element-773">
                <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center" data-id="element-774">
                  <Package className="h-6 w-6 text-gray-400" data-id="element-775" />
                </div>
                <div className="flex-1" data-id="element-776">
                  <h4 className="font-medium text-gray-900" data-id="element-777">
                    Performance T-Shirt
                  </h4>
                  <p className="text-sm text-gray-600" data-id="element-778">Adult L / Navy</p>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-779">
                    Personalization: SMITH #23
                  </p>
                </div>
                <div className="text-right" data-id="element-780">
                  <p className="font-medium text-gray-900" data-id="element-781">$29.99</p>
                  <p className="text-sm text-gray-500" data-id="element-782">Qty: 2</p>
                </div>
              </div>

              <div className="flex items-start space-x-4" data-id="element-783">
                <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center" data-id="element-784">
                  <Package className="h-6 w-6 text-gray-400" data-id="element-785" />
                </div>
                <div className="flex-1" data-id="element-786">
                  <h4 className="font-medium text-gray-900" data-id="element-787">Team Hoodie</h4>
                  <p className="text-sm text-gray-600" data-id="element-788">Adult M / Gray</p>
                </div>
                <div className="text-right" data-id="element-789">
                  <p className="font-medium text-gray-900" data-id="element-790">$54.99</p>
                  <p className="text-sm text-gray-500" data-id="element-791">Qty: 1</p>
                </div>
              </div>
            </div>
          </Card>

          <Card title="Activity Log" data-id="element-792">
            <div className="space-y-3" data-id="element-793">
              {activityLog.map(entry => <div key={entry.id} className="flex items-start space-x-3 text-sm" data-id="element-794">
                  <Clock className="h-4 w-4 text-gray-400 mt-0.5" data-id="element-795" />
                  <div className="flex-1" data-id="element-796">
                    <p className="text-gray-900" data-id="element-797">{entry.action}</p>
                    <p className="text-gray-500 text-xs" data-id="element-798">
                      {new Date(entry.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>)}
            </div>
          </Card>
        </div>

        <div className="space-y-6" data-id="element-799">
          <Card title="Customer" data-id="element-800">
            <div className="space-y-3" data-id="element-801">
              <div data-id="element-802">
                <p className="text-sm text-gray-600" data-id="element-803">Name</p>
                <p className="font-medium text-gray-900" data-id="element-804">
                  {order.customerName}
                </p>
              </div>
              <div data-id="element-805">
                <p className="text-sm text-gray-600" data-id="element-806">Email</p>
                <p className="font-medium text-gray-900" data-id="element-807">
                  {order.customerEmail}
                </p>
              </div>
            </div>
          </Card>

          <Card title="Store" data-id="element-808">
            <div className="space-y-3" data-id="element-809">
              <div data-id="element-810">
                <p className="text-sm text-gray-600" data-id="element-811">Store Name</p>
                <p className="font-medium text-gray-900" data-id="element-812">{order.storeName}</p>
              </div>
              <Link to={`/pop-up-stores/${order.storeId}`} className="text-sm text-red-600 hover:text-red-700 font-medium" data-id="element-813">
                View Store Details →
              </Link>
            </div>
          </Card>
        </div>
      </div>

      <ResendConfirmationModal isOpen={isResendModalOpen} onClose={() => setIsResendModalOpen(false)} customerEmail={order.customerEmail} customerPhone="(555) 123-4567" onResend={handleResendConfirmation} data-id="element-814" />
    </div>;
}